import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Slide {
  id: string;
  title: string;
  desc: string;
  tags: string[];
  image: string;
  to: string;
  label: string;
}

const SLIDES: Slide[] = [
  {
    id: 'slide-1',
    title: '星际远航',
    desc: '人类深空探索舰队穿越虫洞，在浩瀚宇宙中寻找新的家园。年度科幻巨制，口碑持续走高。',
    tags: ['科幻', '动作', '2025'],
    image:
      'https://readdy.ai/api/search-image?query=epic%20sci-fi%20space%20scene%2C%20grand%20spaceship%20fleet%20traveling%20toward%20a%20glowing%20blue%20planet%2C%20vast%20starfield%20nebula%2C%20dramatic%20cinematic%20lighting%2C%20deep%20navy%20and%20amber%20accents%2C%20no%20text&width=1600&height=900&seq=hero-slide-01&orientation=landscape',
    to: '/detail/movie/movie-1',
    label: '重点推荐'
  },
  {
    id: 'slide-2',
    title: '长安风云录',
    desc: '繁华都城之下暗流涌动，年轻御史如何在朝堂博弈中匡扶公道。一部制作考究的历史正剧。',
    tags: ['古装', '历史', '40集'],
    image:
      'https://readdy.ai/api/search-image?query=majestic%20ancient%20chinese%20palace%20at%20dusk%2C%20grand%20courtyard%20with%20red%20pillars%20and%20golden%20roofs%2C%20misty%20dramatic%20sky%2C%20cinematic%20historical%20epic%20mood%2C%20no%20text&width=1600&height=900&seq=hero-slide-02&orientation=landscape',
    to: '/detail/tv/tv-1',
    label: '热播剧集'
  },
  {
    id: 'slide-3',
    title: '迷雾侦探',
    desc: '浓雾笼罩的城市里，一位擅长观察细节的侦探接连破解离奇案件，推理爱好者必看。',
    tags: ['悬疑', '推理', '24集'],
    image:
      'https://readdy.ai/api/search-image?query=mysterious%20foggy%20old%20city%20street%20at%20night%2C%20glowing%20gas%20lamps%2C%20lone%20detective%20in%20long%20coat%2C%20moody%20blue%20and%20amber%20lighting%2C%20cinematic%20suspense%20atmosphere%2C%20no%20text&width=1600&height=900&seq=hero-slide-03&orientation=landscape',
    to: '/detail/tv/tv-3',
    label: '高分推荐'
  },
  {
    id: 'slide-4',
    title: '山海秘境',
    desc: '少年为寻找失踪的师父，踏上穿越上古山脉的奇幻旅程，揭开山海之间的古老秘密。',
    tags: ['奇幻', '冒险', '2024'],
    image:
      'https://readdy.ai/api/search-image?query=mystical%20ancient%20oriental%20mountains%20shrouded%20in%20mist%2C%20glowing%20golden%20light%20among%20tall%20peaks%2C%20epic%20fantasy%20atmosphere%2C%20teal%20and%20amber%20tones%2C%20no%20text&width=1600&height=900&seq=hero-slide-04&orientation=landscape',
    to: '/detail/movie/movie-5',
    label: '奇幻冒险'
  }
];

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);

  const goTo = useCallback((index: number) => {
    setCurrent((index + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % SLIDES.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [current]);

  return (
    <section className="relative h-[460px] w-full overflow-hidden bg-foreground-950 md:h-[600px]">
      {SLIDES.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} 风驰影院影视推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/50 to-foreground-950/20" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1240px] flex-col justify-center px-4 md:px-6">
        <div key={SLIDES[current].id} className="fc-fade-up max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-secondary-950">
            <i className="ri-fire-fill" />
            {SLIDES[current].label}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            {SLIDES[current].title}
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {SLIDES[current].desc}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {SLIDES[current].tags.map((tag) => (
              <span
                key={tag}
                className="rounded-md border border-background-50/30 px-2.5 py-1 text-xs text-background-100"
              >
                {tag}
              </span>
            ))}
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={SLIDES[current].to}
              className="flex h-11 items-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-information-line text-lg" />
              查看详情
            </Link>
            <Link
              to="/category/movie"
              className="flex h-11 items-center gap-2 rounded-md border border-background-50/40 bg-background-50/10 px-6 text-sm font-medium text-background-50 backdrop-blur transition hover:bg-background-50/20 whitespace-nowrap"
            >
              <i className="ri-compass-3-line text-lg" />
              浏览更多
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(current - 1)}
        className="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur transition hover:bg-primary-500 md:left-6"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => goTo(current + 1)}
        className="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur transition hover:bg-primary-500 md:right-6"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-16 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {SLIDES.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(index)}
            className={`h-1.5 rounded-full transition-all ${
              index === current ? 'w-7 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
            aria-label={`切换到第 ${index + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}